import { Icon, Progress, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { AVERAGE_SEO_SCORE_OF_TOP_100_WEBSITES } from '../../../settings/config';
import { ScoreData } from '../types';
import { getColorByScore, getSectionsProgress } from '../utils';
import SeoScoreDescription from './SeoScoreDescription';

type Props = {
    scoreData: ScoreData;
    totalSectionsCount: number;
    isAnalysisInProgress?: boolean;
    isFullReport?: boolean;
};

export default function SeoScore({ scoreData, totalSectionsCount, isAnalysisInProgress, isFullReport }: Props) {
    const sectionsProgress = useMemo(() => getSectionsProgress(scoreData, totalSectionsCount), [scoreData, totalSectionsCount]);

    return (
        <div className={`summary-graph ${isAnalysisInProgress ? 'in-progress' : ''}`}>
            <div className="summary-graph-wrapper">
                <div className="summary-graph-total">
                    <Progress
                        type="circle"
                        percent={scoreData.score}
                        strokeWidth={8}
                        strokeColor={getColorByScore(scoreData.score)}
                        format={(percent) => `${percent}/100`}
                        data-cy="main-url-score"
                    />
                    <span className="progress-label">SEO Score</span>
                </div>

                {isFullReport && (
                    <div className="avg-score">
                        <span>Average SEO score of top 100 sites: {AVERAGE_SEO_SCORE_OF_TOP_100_WEBSITES}%</span>
                        <Tooltip
                            placement="right"
                            title={'This value indicates the average SEO score of the top 100 most visited websites in the US (in the past 12 months)'}
                        >
                            <Icon
                                type="info-circle"
                                theme="filled"
                                className="info-icon"
                            />
                        </Tooltip>
                    </div>
                )}
            </div>

            <div className="summary-graph-split">
                {isFullReport && (
                    <div className="score-overview">
                        <SeoScoreDescription
                            seoScore={scoreData.score}
                            issues={scoreData.failed}
                        />
                    </div>
                )}

                <div
                    className="progress-item failed"
                    data-cy="main-url-failed-counter"
                >
                    <span className="progress-label">
                        <strong>{scoreData.failed}</strong> Failed
                    </span>
                    <Progress
                        percent={sectionsProgress.failed}
                        showInfo={false}
                    />
                </div>

                <div className="progress-item warning">
                    <span
                        className="progress-label"
                        data-cy="main-url-warning-counter"
                    >
                        <strong>{scoreData.warning}</strong> Warnings
                    </span>
                    <Progress
                        percent={sectionsProgress.warning}
                        showInfo={false}
                    />
                </div>

                <div className="progress-item passed">
                    <span
                        className="progress-label"
                        data-cy="main-url-passed-counter"
                    >
                        <strong>{scoreData.passed}</strong> Passed
                    </span>
                    <Progress
                        percent={sectionsProgress.passed}
                        showInfo={false}
                    />
                </div>
            </div>
        </div>
    );
}
